/**
 * Pure utility for Parsons-condition checks on a canvas argument diagram.
 *
 * The diagram is interpreted as follows:
 *
 * - Rectangles are statements (Parsons-style "steps" in the reasoning
 *   structure).
 * - Only arrows with `kind === "directed"` (and not `doNotRender`) contribute
 *   to the directed dependency graph. "undirected" arrows are scaffolding
 *   and "bidirectional" arrows are annotations of mutual entailment; both
 *   are ignored for evaluation.
 * - A rectangle's category determines its role:
 *   - setting     → an assumption in the Parsons setting
 *   - target      → the designated conclusion
 *   - marginalia  → excluded from analysis (e.g. "Question" nodes)
 *   - anything else → a step in the reasoning structure (premise if it has
 *     no incoming directed arrows, inferred step if any)
 *
 * Conditions checked (all rectangle-level diagnostics):
 *
 * - [1] Premise-in-setting. Every node with no incoming directed arrows
 *   (a structural premise) must be tagged setting. Setting nodes must NOT
 *   themselves be inferred (no incoming directed arrows). Skipped entirely
 *   when no setting nodes exist; the UI surfaces a separate hint in that
 *   case.
 * - [3] Target = conclusion. Target nodes must be chain-ends (no outgoing
 *   directed arrows). At most one target may exist. Non-target, non-setting,
 *   non-marginalia chain-ends with at least one supporter suggest argument
 *   drift.
 * - [4] Noncircular. No directed cycles in the dependency graph.
 *
 * Conditions [2] and [5] are deliberately not checked here. [2] (every
 * inference accords with a principle in the setting) requires an
 * inference-principle inventory that lives in future work. [5] (no infinite
 * regress) reduces to [1] + [4] in finite reasoning structures and is
 * addressed in the companion paper rather than enforced on the canvas.
 */

import type { ArrowKind } from "@/types/canvasTypes";

export type ConditionId = "1" | "3" | "4";

/**
 * Informal-logic fallacy a diagnostic corresponds to. Used by the UI to
 * pick visual treatment (e.g., petitio principii is structurally a cycle
 * involving the target, so it should glow red rather than the orange
 * normally used for [3] target/conclusion issues).
 */
export type FallacyName =
  | "begging_the_question"
  | "petitio_principii"
  | "ignoratio_elenchi"
  | "ambiguous_conclusion"
  | "circular_reasoning";

export interface NodeDiagnostic {
  rectIdx: number;
  condition: ConditionId;
  message: string;
  fallacy?: FallacyName;
  /**
   * When set, the diagnostic was produced by evaluating a single
   * CanvasGroup as a self-contained sub-argument. The id refers to
   * the group whose frame the UI should anchor any group-scoped chip
   * to. Diagnostics with no groupId come from the canvas-wide pass
   * over ungrouped rectangles.
   */
  groupId?: string;
}

export interface RoleLabels {
  setting: string;
  target: string;
  marginalia: string;
}

/**
 * Structural minimum the checks need from an arrow. The canvas's actual
 * arrow types (ICanvasArrow, CanvasArrow) both satisfy this shape.
 */
export interface CheckableArrow {
  endRectIdx: number;
  kind: ArrowKind;
  doNotRender?: boolean;
}

/**
 * Structural minimum the checks need from a rectangle. `category` is optional
 * because the workspace.ts Rectangle type does not declare it, even though
 * runtime data carries it.
 */
export interface CheckableRectangle {
  category?: string;
  arrows: ReadonlyArray<CheckableArrow>;
}

interface AdjacencyLists {
  /** Outgoing directed neighbors per rect, indexed by source rect. */
  outgoing: number[][];
  /** Incoming directed neighbors per rect, indexed by destination rect. */
  incoming: number[][];
}

function buildDirectedAdjacency(
  rectangles: ReadonlyArray<CheckableRectangle>
): AdjacencyLists {
  const outgoing: number[][] = rectangles.map(() => []);
  const incoming: number[][] = rectangles.map(() => []);
  for (let i = 0; i < rectangles.length; i++) {
    const rect = rectangles[i];
    if (!rect) continue;
    for (const arr of rect.arrows) {
      if (arr.kind !== "directed" || arr.doNotRender) continue;
      const dst = arr.endRectIdx;
      if (dst < 0 || dst >= rectangles.length) continue;
      outgoing[i]?.push(dst);
      incoming[dst]?.push(i);
    }
  }
  return { outgoing, incoming };
}

/**
 * Returns the set of rect indices participating in any directed cycle.
 * A node is in a cycle iff there is a directed path from it back to itself.
 * O(V * (V + E)) — fine for the canvases this tool is built for (<100 nodes).
 */
export function detectCycles(
  rectangles: ReadonlyArray<CheckableRectangle>
): Set<number> {
  const { outgoing } = buildDirectedAdjacency(rectangles);
  const inCycle = new Set<number>();

  for (let start = 0; start < rectangles.length; start++) {
    if (inCycle.has(start)) continue;
    // DFS from `start`; if we ever step onto `start` again, it's in a cycle.
    const visited = new Set<number>();
    const stack: number[] = [start];
    let found = false;
    while (stack.length > 0 && !found) {
      const u = stack.pop()!;
      const neighbors = outgoing[u] ?? [];
      for (const v of neighbors) {
        if (v === start) {
          found = true;
          break;
        }
        if (!visited.has(v)) {
          visited.add(v);
          stack.push(v);
        }
      }
    }
    if (found) inCycle.add(start);
  }

  return inCycle;
}

/**
 * Condition [4] diagnostics: nodes participating in a directed cycle.
 * `exclude` lets the orchestrator suppress entries for nodes that are
 * already being reported under a more specific fallacy (e.g., a target
 * node in a cycle is petitio principii, not generic circular reasoning).
 */
export function findCycleDiagnostics(
  rectangles: ReadonlyArray<CheckableRectangle>,
  exclude: ReadonlySet<number> = new Set()
): NodeDiagnostic[] {
  return Array.from(detectCycles(rectangles))
    .filter((rectIdx) => !exclude.has(rectIdx))
    .map((rectIdx) => ({
      rectIdx,
      condition: "4" as const,
      fallacy: "circular_reasoning",
      message:
        "Circular reasoning: this step's supports trace back to itself.",
    }));
}

/**
 * Condition [1] diagnostics: premises must be in the setting; setting nodes
 * must not themselves be inferred. Returns an empty array when no setting
 * nodes exist (so the UI can surface that case as a separate hint rather
 * than flagging every non-setting node).
 */
export function findPremiseInSettingDiagnostics(
  rectangles: ReadonlyArray<CheckableRectangle>,
  roles: RoleLabels
): NodeDiagnostic[] {
  const hasSettingNode = rectangles.some(
    (r) => r.category === roles.setting
  );
  if (!hasSettingNode) return [];

  const { incoming } = buildDirectedAdjacency(rectangles);
  const out: NodeDiagnostic[] = [];

  for (let i = 0; i < rectangles.length; i++) {
    const cat = rectangles[i]?.category ?? "";
    if (cat === roles.marginalia) continue;
    const hasIncoming = (incoming[i]?.length ?? 0) > 0;

    if (cat === roles.setting) {
      if (hasIncoming) {
        out.push({
          rectIdx: i,
          condition: "1",
          message: `${roles.setting} nodes shouldn't have incoming arrows.`,
        });
      }
    } else if (!hasIncoming) {
      out.push({
        rectIdx: i,
        condition: "1",
        fallacy: "begging_the_question",
        message: `Begging the question: no incoming support. Tag as ${roles.setting} or add a step that supports it.`,
      });
    }
  }

  return out;
}

/**
 * Condition [3] diagnostics: target nodes must be chain-ends, unique, and
 * the only chain-ends in the reasoning structure (modulo setting/marginalia).
 *
 * If `cycleSet` is supplied, the target-with-outgoing case is split into
 * petitio principii (the target is part of a directed cycle — the chain
 * returns to it) versus ignoratio elenchi (the target is used as a premise
 * but the chain doesn't return). Without `cycleSet`, the older generic
 * label is used for backward compatibility with callers.
 */
export function findTargetIssueDiagnostics(
  rectangles: ReadonlyArray<CheckableRectangle>,
  roles: RoleLabels,
  cycleSet: ReadonlySet<number> = new Set()
): NodeDiagnostic[] {
  const { outgoing, incoming } = buildDirectedAdjacency(rectangles);
  const out: NodeDiagnostic[] = [];

  const targetIndices: number[] = [];
  for (let i = 0; i < rectangles.length; i++) {
    if (rectangles[i]?.category === roles.target) {
      targetIndices.push(i);
    }
  }

  if (targetIndices.length > 1) {
    for (const i of targetIndices) {
      out.push({
        rectIdx: i,
        condition: "3",
        fallacy: "ambiguous_conclusion",
        message: `Ambiguous conclusion: multiple ${roles.target} nodes. The reasoning structure should have one designated conclusion.`,
      });
    }
  }

  for (const i of targetIndices) {
    if ((outgoing[i]?.length ?? 0) > 0) {
      if (cycleSet.has(i)) {
        // Target has outgoing arrows AND the chain returns to it — the
        // target is being used to derive itself. Parsons §V identifies
        // this as petitio principii, a particular sub-case of begging
        // the question.
        out.push({
          rectIdx: i,
          condition: "3",
          fallacy: "petitio_principii",
          message: `Petitio principii: the ${roles.target} is being used to support a step that supports the ${roles.target}.`,
        });
      } else {
        // Target has outgoing arrows but the chain doesn't return — the
        // argument concludes somewhere other than the stated target.
        out.push({
          rectIdx: i,
          condition: "3",
          fallacy: "ignoratio_elenchi",
          message: `Ignoratio elenchi: the ${roles.target} is being used as a premise — the chain concludes somewhere other than the stated goal.`,
        });
      }
    }
  }

  for (let i = 0; i < rectangles.length; i++) {
    const cat = rectangles[i]?.category ?? "";
    if (
      cat === roles.target ||
      cat === roles.setting ||
      cat === roles.marginalia
    ) {
      continue;
    }
    const outDeg = outgoing[i]?.length ?? 0;
    const inDeg = incoming[i]?.length ?? 0;
    if (outDeg === 0 && inDeg > 0) {
      out.push({
        rectIdx: i,
        condition: "3",
        fallacy: "ignoratio_elenchi",
        message: `Ignoratio elenchi: chain terminates here, but this step isn't tagged as ${roles.target}.`,
      });
    }
  }

  return out;
}

export interface EvaluationResult {
  diagnostics: NodeDiagnostic[];
  /** Map from rect index to all diagnostics flagging that rect. */
  byRect: Map<number, NodeDiagnostic[]>;
  /** Conditions that produced at least one diagnostic. */
  violatedConditions: Set<ConditionId>;
}

/**
 * Runs every condition check and returns combined results. Orchestrates
 * the petitio-principii combined case: a target node that is in a cycle
 * AND has outgoing arrows is petitio (not generic circular reasoning),
 * so we suppress the [4] cycle entry for that node and let the [3]
 * target-issue path emit the precise petitio message.
 */
export function evaluateArgument(
  rectangles: ReadonlyArray<CheckableRectangle>,
  roles: RoleLabels
): EvaluationResult {
  const cycleSet = detectCycles(rectangles);

  // Build outgoing map for the petitio-target check (target has outgoing
  // arrows AND is in the cycle).
  const { outgoing } = buildDirectedAdjacency(rectangles);
  const petitioTargets = new Set<number>();
  for (let i = 0; i < rectangles.length; i++) {
    if (
      rectangles[i]?.category === roles.target &&
      cycleSet.has(i) &&
      (outgoing[i]?.length ?? 0) > 0
    ) {
      petitioTargets.add(i);
    }
  }

  const diagnostics: NodeDiagnostic[] = [
    // Skip cycle diagnostics on petitio targets — the [3] path will
    // emit the precise petitio message for those nodes.
    ...findCycleDiagnosticsFromSet(cycleSet, petitioTargets),
    ...findPremiseInSettingDiagnostics(rectangles, roles),
    ...findTargetIssueDiagnostics(rectangles, roles, cycleSet),
  ];

  const byRect = new Map<number, NodeDiagnostic[]>();
  const violatedConditions = new Set<ConditionId>();
  for (const d of diagnostics) {
    const list = byRect.get(d.rectIdx);
    if (list) list.push(d);
    else byRect.set(d.rectIdx, [d]);
    violatedConditions.add(d.condition);
  }

  return { diagnostics, byRect, violatedConditions };
}

/**
 * Helper used by `evaluateArgument` to reuse a precomputed cycle set
 * (we already computed it for the petitio combined check; recomputing
 * inside `findCycleDiagnostics` would be wasteful).
 */
function findCycleDiagnosticsFromSet(
  cycleSet: ReadonlySet<number>,
  exclude: ReadonlySet<number>
): NodeDiagnostic[] {
  return Array.from(cycleSet)
    .filter((rectIdx) => !exclude.has(rectIdx))
    .map((rectIdx) => ({
      rectIdx,
      condition: "4" as const,
      fallacy: "circular_reasoning",
      message:
        "Circular reasoning: this step's supports trace back to itself.",
    }));
}
